import { logger as baseLogger } from "matrix-js-sdk/src/logger";
import { io, Socket } from "socket.io-client";
import { base64ToBytes, bytesToBase64, timingSafeEqual } from "../commons/cryptoUtils";
import { AESType, getAESInstance, IAES } from "../commons/AESManagerFactory";
import { PeerPairingClientV2 } from "./PeerPairingClientV2";
import { CallKeyExchangeCompletedInfo, ICallKeyExchangeManager, RealtimeGatewayOptions } from "../commons/CallKeyExchangeManagerFactory";

const logger = baseLogger.getChild("CallKeyExchangeManagerV2");

/**
 * Gestisce la connessione al gateway realtime per lo scambio chiavi E2EE tra MASTER e PEER.
 * Utilizza socket.io per la comunicazione e PeerPairingClient per lo scambio chiavi ECDH.
 * Supporta i ruoli MASTER (fornisce la chiave LiveKit) e PEER (richiede la chiave LiveKit).
 */
export class CallKeyExchangeManagerV2 implements ICallKeyExchangeManager {
    private readonly AES_TYPE: AESType = AESType.AES_GCM;

    private socket!: Socket;
    private pairing!: PeerPairingClientV2;
    private connected: boolean = false;
    private authenticated: boolean = false;
    private handlersRegistered: boolean = false;
    private forceDisconnect: boolean = false;
    private lastDerivedConfirmationKey?: Uint8Array; // usata per la conferma chiave (MITM detection)


    /**
     * Crea una nuova istanza di CallKeyExchangeManagerV2.
     * @param options Opzioni di configurazione per la connessione realtime.
     * @throws Se il ruolo è MASTER e manca la liveKitKey.
     */
    constructor(
        private readonly options: RealtimeGatewayOptions
    ) {
        // Genera una liveKitKey casuale se ruolo MASTER
        if (options.role === "MASTER" && !options.liveKitKey) {
            throw new Error("MASTER role requires liveKitKey");
        }

        // Rimuove slash finali dall'URL        
        options.url = options.url.replace(/\/+$/, "");
    }


    // #region Connection & Auth

    /**
     * Connette il client al gateway realtime con autenticazione integrata (auth param nella connessione).
     * Esegue direttamente join del canale e registra gli handler.
     * 
     * @param requestKeyExchange Se true e ruolo PEER, richiede lo scambio chiavi dopo il join del canale.
     * @throws Se già connesso.
     * @returns Promise risolta se connessione e join avvenuti con successo.
     */
    async connectAndJoinChannel(requestKeyExchange: boolean = true): Promise<boolean> {
        await this.internalConnect({ withAuth: true });

        // Esegue join del canale
        await this.joinChannel();

        // Registra gli handler per lo scambio chiavi
        this.registerKeyExchangeHandlers();

        // Se ruolo PEER, richiede lo scambio chiavi
        if (this.options.role === "PEER" && requestKeyExchange) {
            await this.requestKeyExchange();
        }

        return true;
    }

    /**
     * Connette il client al gateway realtime SENZA autenticazione automatica.
     * Dopo la connessione, è necessario chiamare loginAndJoinChannel() manualmente.
     */
    async connectOnly(): Promise<void> {
        await this.internalConnect({ withAuth: false });
        // Qui NON viene fatto login e joinChannel automaticamente
        // L'utente dovrà chiamare manualmente loginAndJoinChannel()
    }

    /**
     * Logica comune di connessione per connectAndJoinChannel e connectOnly.
     * @param options.withAuth Se true, connette con autenticazione.
     */
    private async internalConnect({ withAuth }: { withAuth: boolean }): Promise<void> {
        if (this.connected) {
            throw new Error("Already connected");
        }

        logger.debug("Connecting to", this.options.url, "with auth:", withAuth);

        // Inizializza PeerPairingClient
        this.pairing = new PeerPairingClientV2();

        // Connette il socket con o senza auth
        this.socket = io(`${this.options.url}/rt`, {
            forceNew: true,
            reconnection: true,
            secure: true,
            transports: ["websocket"],
            reconnectionAttempts: 10,
            reconnectionDelay: 100,
            reconnectionDelayMax: 500,
            autoConnect: true,
            auth: withAuth ? { token: this.options.token } : undefined,
        });

        // Attende la connessione
        await this.waitForConnect();

        // Imposta stato
        this.connected = true;
        this.authenticated = withAuth;

        logger.debug("Connected.");

        //#region Debug logs
        this.socket.on("connect", () => {
            logger.info("Socket connected: connection reestablished.");
        });
        this.socket.on("disconnect", (reason: string) => {
            logger.warn("Socket disconnected:", reason);
            // Se la disconnessione è causata da "io server disconnect", 
            // significa che il server ha forzato la disconnessione,
            // quindi in questo caso vogliamo forzare una riconnessione automatica.
            if (reason === "io server disconnect") {
                // Disconnetto prima di riconnettere
                this.disconnect();

                // Se non forzato a disconnettersi, tenta di riconnettersi
                if (!this.forceDisconnect) {
                    // Forza la riconnessione automatica
                    logger.info("Forzo riconnessione dopo 'io server disconnect'");
                    // Riconnetto e rifaccio join canale
                    this.connectAndJoinChannel();
                }
            }
        });
        this.socket.on("connect_error", (err: any) => {
            logger.error("Socket connect_error:", err);
        });
        // this.socket.on("reconnect_attempt", (attempt: number) => {
        //     console.info("[CallKeyExchangeManagerV2] Socket reconnect_attempt:", attempt);
        // });
        // this.socket.on("reconnect", (attempt: number) => {
        //     console.info("[CallKeyExchangeManagerV2] Socket reconnect success:", attempt);
        // });
        // this.socket.on("ping", () => {
        //     console.debug("[CallKeyExchangeManagerV2] socket.io native ping sent");
        // });
        // this.socket.on("pong", (latency: number) => {
        //     console.debug("[CallKeyExchangeManagerV2] socket.io native pong received. Latency:", latency);
        // });
        // // Log ogni evento generico ricevuto (debug avanzato)
        // this.socket.onAny((event, ...args) => {
        //     if (event !== "ping" && event !== "pong") {
        //         console.debug(`[CallKeyExchangeManagerV2] socket.io event: ${event}`, ...args);
        //     }
        // });

        //#endregion
    }

    /**
     * Esegue login e join del canale dopo una connessione senza auth.
     * @param requestKeyExchange Se true e ruolo PEER, richiede lo scambio chiavi dopo il join del canale.
     * @throws Se non connesso o già autenticato.
     * @returns Promise risolta se login e join avvenuti con successo.
     */
    async loginAndJoinChannel(requestKeyExchange: boolean = true): Promise<void> {
        if (!this.connected) {
            throw new Error("Not connected");
        }
        if (this.authenticated) {
            throw new Error("Already authenticated");
        }

        // Esegue login
        await this.login();
        this.authenticated = true;

        // Esegue join del canale
        await this.joinChannel();

        // Registra gli handler per lo scambio chiavi
        this.registerKeyExchangeHandlers();

        // Se ruolo PEER, richiede lo scambio chiavi
        if (this.options.role === "PEER" && requestKeyExchange) {
            await this.requestKeyExchange();
        }
    }

    /**
     * Esegue il login esplicito tramite socket (da usare dopo connectWithoutAuth).
     * @returns Promise risolta se login avvenuto con successo.
     */
    private async login(): Promise<void> {
        if (!this.connected) {
            throw new Error("Cannot login: not connected");
        }
        return new Promise<void>((resolve, reject) => {
            this.socket.emit(
                "auth:login",
                { token: this.options.token },
                (ack: { ok: boolean }) => {
                    ack?.ok ? resolve() : reject(new Error("Login failed"));
                }
            );
        });
    }

    /**
     * Attende la connessione del socket.
     * @returns Promise risolta alla connessione.
     */
    private waitForConnect(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.socket.once("connect", resolve);
            this.socket.once("connect_error", reject);
        });
    }

    // #endregion


    // #region Channel

    /**
     * Effettua il join del canale specificato nelle opzioni.
     * @returns Promise risolta se join avvenuto con successo.
     */
    private async joinChannel(): Promise<void> {
        if (!this.connected) {
            throw new Error("Cannot join channel: not connected");
        }
        if (!this.authenticated) {
            throw new Error("Cannot join channel: not authenticated");
        }
        return new Promise<void>((resolve, reject) => {
            this.socket.emit(
                "channel:join",
                {
                    channel: this.options.channel,
                    role: this.options.role,
                },
                (ack: { ok: boolean }) => {
                    ack?.ok ? resolve() : reject(new Error("Channel join failed"));
                }
            );
        });
    }

    /**
     * Lascia il canale corrente.
     */
    leaveChannel(): void {
        if (!this.connected) {
            throw new Error("Cannot leave channel: not connected");
        }
        if (!this.authenticated) {
            throw new Error("Cannot leave channel: not authenticated");
        }
        this.socket.emit("channel:leave", {
            channel: this.options.channel,
        });
    }

    // #endregion


    // #region Key exchange

    /**
     * Richiede lo scambio chiavi (solo PEER).
     * @param timeoutMs Timeout massimo di attesa risposta dal MASTER. 
     * se <=0, attende indefinitamente.
     * @throws Se il ruolo non è PEER.
     * @returns Promise risolta se la richiesta va a buon fine.
     */
    async requestKeyExchange(timeoutMs: number = 0): Promise<void> {
        // Solo i PEER possono richiedere lo scambio chiavi
        if (this.options.role !== "PEER") {
            throw new Error("Only PEER can request key exchange");
        }

        // Prepara la chiave pubblica compressa del PEER da inviare al MASTER
        const publicKey: string = bytesToBase64(this.pairing.getCompressedPublicKey());

        logger.debug("Requesting key exchange with MASTER. Peer Public Key:", publicKey);

        // Invia la richiesta di scambio chiavi al MASTER con la chiave pubblica del PEER
        const emitPromise: Promise<void> = new Promise<void>((resolve, reject) => {
            this.socket.emit(
                "key_exchange:request",
                {
                    channel: this.options.channel,
                    publicKey,
                },
                (ack: { ok: boolean }) => {
                    ack?.ok
                        ? resolve()
                        : reject(new Error("Key exchange request rejected"));
                }
            );
        });

        // Gestisce il timeout se specificato
        if (timeoutMs > 0) {
            const timeoutPromise: Promise<void> = new Promise<void>((_, reject) =>
                setTimeout(
                    () => reject(new Error("Key exchange timeout")),
                    timeoutMs
                )
            );

            return Promise.race([emitPromise, timeoutPromise]);
        }

        // Altrimenti torna direttamente la promise dell'emit 
        return emitPromise;
    }

    /**
     * Registra gli handler per lo scambio chiavi in base al ruolo.
     * Evita registrazioni duplicate.
     */
    private registerKeyExchangeHandlers(): void {
        // Evita di registrare più volte gli handler in caso di riconnessioni
        if (this.handlersRegistered) return;
        this.handlersRegistered = true;

        // Registra gli handler specifici per MASTER o PEER. 
        if (this.options.role === "MASTER") {
            this.registerMasterHandlers();
        } else {
            this.registerPeerHandlers();
        }
    }

    /**
     * Valida e decodifica una chiave pubblica ECDH in formato Base64.
     * Curve: P-521 → chiave compressa = 67 byte.
     * @param base64 Chiave pubblica Base64.
     */
    private validatePublicKey(base64: string): Uint8Array {
        // Decodifica la chiave pubblica da Base64 a Uint8Array
        const buf = base64ToBytes(base64);

        // Verifica la lunghezza della chiave pubblica (P-521 compressed = 67 byte)
        if (buf.length !== 67) {
            throw new Error("Invalid ECDH public key length");
        }

        return buf;
    }

    // #region MASTER

    /**
     * Registra gli handler per la gestione delle richieste di scambio chiavi lato MASTER.
     */
    private registerMasterHandlers(): void {
        // Handler per la richiesta di scambio chiavi da parte del PEER
        this.socket.on(
            "key_exchange:request",
            async ({ fromSocketId, publicKey }: { fromSocketId: string; publicKey: string }) => {
                logger.debug("key_exchange:request event received. Socket connected:", this.socket.connected);

                // Valida e decodifica la chiave pubblica del peer
                const peerPublicKey = this.validatePublicKey(publicKey);

                logger.debug("Peer Public Key received:", bytesToBase64(peerPublicKey));

                // Deriva i parametri AES (encryptionKey, confirmationKey, sharedSecret) usando ECDH + HKDF
                const {
                    encryptionKey,
                    confirmationKey,
                    sharedSecret
                } = await this.pairing.deriveAESParameters(
                    peerPublicKey,
                    this.options.channel
                );

                logger.debug("AES Parameters derived:", {
                    encryptionKey: bytesToBase64(encryptionKey),
                    confirmationKey: bytesToBase64(confirmationKey),
                    sharedSecret: bytesToBase64(sharedSecret)
                });

                // salva la chiave di conferma per la fase di conferma chiave (MITM detection)
                this.lastDerivedConfirmationKey = confirmationKey;

                // Cripta la chiave LiveKit con AES usando encryptionKey derivata
                const aes: IAES = await getAESInstance(
                    this.AES_TYPE,
                    encryptionKey,
                    new Uint8Array(0) // ignored in GCM
                );

                // Cifra la liveKitKey
                const encryptedLiveKitKeyBytes = await aes.encrypt(
                    this.options.liveKitKey!
                );

                // Codifica la chiave cifrata in Base64 per l'invio
                const encryptedLiveKitKey = bytesToBase64(
                    encryptedLiveKitKeyBytes
                );

                // Prepara la chiave pubblica del MASTER in Base64 per l'invio al PEER
                const masterPublicKeyBuf = bytesToBase64(
                    this.pairing.getCompressedPublicKey()
                );

                logger.debug("LiveKit E2EE Key Encrypted generated:", encryptedLiveKitKey);
                logger.debug("LiveKit E2EE Public Key generated:", masterPublicKeyBuf);

                // Generazione codice SAS
                const sasCode = await this.pairing.generateSasCode(
                    sharedSecret,
                    this.options.channel,
                    peerPublicKey
                );

                logger.debug("SAS Code (MASTER):", sasCode);

                // MASTER already knows the LiveKit key
                this.onKeyExchangeCompleted?.({ liveKitKey: this.options.liveKitKey!, sasCode });

                this.socket.emit(
                    "key_exchange:response",
                    {
                        channel: this.options.channel,
                        toSocketId: fromSocketId,
                        encryptedLiveKitKey,
                        masterPublicKey: masterPublicKeyBuf,
                    },
                    (ack: { ok: boolean }) => {
                        if (!ack?.ok) {
                            logger.error("Key exchange response failed");
                        }
                    }
                );

                // Per adesso commento la disconnessione forzata dopo l'invio della chiave 
                // perchè può servire finchè la chiamata è attiva
                // // in modo da poter testare meglio la fase di conferma chiave e rilevamento MITM.
                // // Forza la disconnessione dopo aver inviato la chiave
                // // 3 secondi di ritardo per garantire l'invio e la ricezione della risposta
                // setTimeout(() => {
                //     console.info("[CallKeyExchangeManagerV2] Disconnessione dopo invio chiave E2EE LiveKit al PEER.");
                //     this.forceDisconnect = true;
                // }, 3000);
            }
        );

        // Handler per la conferma della chiave da parte del PEER (opzionale, ma consigliato per rilevare MITM)
        this.socket.on(
            "key_confirmation",
            async ({ digest }: { digest: string }) => {

                // Se non abbiamo il contesto necessario per la conferma, logga un errore e esci
                if (!this.lastDerivedConfirmationKey || !this.options.liveKitKey) {
                    logger.error("Missing confirmation context");
                    return;
                }

                // Calcola il digest atteso usando la chiave di conferma derivata e la chiave LiveKit
                const expectedDigest = await this.pairing.computeKeyConfirmation(
                    this.options.liveKitKey,
                    this.lastDerivedConfirmationKey
                );

                // Decodifica il digest ricevuto dal PEER da Base64 a Uint8Array
                const receivedDigest = base64ToBytes(digest);

                // Confronta i digest in modo sicuro (evitando timing attacks)
                const match =
                    expectedDigest.length === receivedDigest.length &&
                    timingSafeEqual(expectedDigest, receivedDigest);

                if (!match) {
                    logger.error("Key confirmation FAILED - possible MITM");
                    return;
                }

                logger.debug("Key confirmation successful");
            }
        );
    }

    // #endregion


    // #region PEER

    /**
     * Registra gli handler per la gestione della risposta di scambio chiavi lato PEER.
     */
    private registerPeerHandlers(): void {
        this.socket.on(
            "key_exchange:response",
            async ({ encryptedLiveKitKey, masterPublicKey }: {
                encryptedLiveKitKey: string;
                masterPublicKey: string;
            }) => {

                // Valida e decodifica la chiave pubblica del MASTER
                const masterPublicKeyBuf =
                    this.validatePublicKey(masterPublicKey);

                // Deriva i parametri AES (encryptionKey, confirmationKey, sharedSecret) usando ECDH + HKDF
                const {
                    encryptionKey,
                    confirmationKey,
                    sharedSecret
                } = await this.pairing.deriveAESParameters(
                    masterPublicKeyBuf,
                    this.options.channel
                );

                // salva la chiave di conferma per la fase di conferma chiave (MITM detection)
                this.lastDerivedConfirmationKey = confirmationKey;

                // Decripta la chiave LiveKit usando AES con encryptionKey derivata
                const aes: IAES = await getAESInstance(
                    this.AES_TYPE,
                    encryptionKey,
                    new Uint8Array(0) // ignored in GCM
                );

                // Decifra la chiave LiveKit
                const liveKitKey = await aes.decrypt(
                    base64ToBytes(encryptedLiveKitKey)
                );

                logger.debug("LiveKit E2EE Key Decrypted:", bytesToBase64(liveKitKey));

                // Generazione codice SAS
                const sasCode = await this.pairing.generateSasCode(
                    sharedSecret,
                    this.options.channel,
                    masterPublicKeyBuf
                );

                logger.debug("SAS Code (PEER):", sasCode);

                // Calcola il digest di conferma chiave usando la chiave di conferma derivata e la chiave LiveKit
                const digest = await this.pairing.computeKeyConfirmation(
                    liveKitKey,
                    confirmationKey
                );

                // Invia la conferma della chiave al MASTER (opzionale, ma consigliato per rilevare MITM)
                this.socket.emit("key_confirmation", {
                    channel: this.options.channel,
                    digest: bytesToBase64(digest),
                });

                logger.debug("Sent key confirmation to MASTER. Digest:", bytesToBase64(digest));

                // Notifica l'applicazione con la chiave LiveKit decriptata
                this.onKeyExchangeCompleted?.({ liveKitKey, sasCode });
            }
        );
    }

    // #endregion
    // #endregion

    // #region Hooks

    /**
     * Hook chiamato lato PEER quando la chiave LiveKit è pronta.
     */
    onKeyExchangeCompleted?: (info: CallKeyExchangeCompletedInfo) => void;

    // #endregion


    // #region Cleanup

    /**
     * Disconnette il client dal gateway realtime e libera le risorse.
     */
    disconnect(): void {
        // console.debug("[CallKeyExchangeManagerV2] disconnecting...");
        if (!this.connected) return;
        logger.debug("disconnected.");

        // Rimuove tutti gli handler registrati per evitare memory leaks e 
        // comportamenti indesiderati in caso di riconnessioni
        this.socket.off("key_exchange:request");
        this.socket.off("key_exchange:response");
        this.socket.off("key_confirmation");
        this.socket.removeAllListeners();

        try {
            if (this.connected && this.authenticated)
                this.leaveChannel();

            if (this.connected)
                this.socket.disconnect();
        } catch (e) {
            logger.error("Error during disconnect:", e);
        }

        this.connected = false;
        this.handlersRegistered = false;
    }

    // #endregion
}
